import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  buildDefaultAffinityChartFilters,
  createEmptyAffinityChartFilters,
  createEmptyAffinityChartSelection,
  type AffinityChartAccessState,
  type AffinityChartFilterType,
  type AffinityChartFilters,
  type AffinityChartLayoutPositions,
  type AffinityChartSelection,
  type AffinityChartSnapshot,
} from "@/features/affinity-chart/affinity-chart.types";
import { clampAffinityChartSelection } from "@/features/affinity-chart/affinity-chart-elements";
import {
  deleteAffinityChartLayout,
  formatAffinityChartErrorMessage,
  getAffinityChartTierPrompt,
  loadAffinityChartSnapshot,
  saveAffinityChartLayout,
} from "@/features/affinity-chart/affinity-chart.client";
import type { SubscriptionTier } from "@/types/projects";

export interface ProjectAffinityChartDependencies {
  deleteAffinityChartLayout: typeof deleteAffinityChartLayout;
  loadAffinityChartSnapshot: typeof loadAffinityChartSnapshot;
  saveAffinityChartLayout: typeof saveAffinityChartLayout;
}

export interface ProjectAffinityChartNotice {
  kind: "error" | "success";
  message: string;
}

export interface ProjectAffinityChartStoreState {
  access: AffinityChartAccessState;
  clearSelection: () => void;
  errorMessage: string | null;
  filters: AffinityChartFilters;
  initializeWorkspace: (projectId: string, tier: SubscriptionTier) => Promise<void>;
  layoutRevision: number;
  notice: ProjectAffinityChartNotice | null;
  positions: AffinityChartLayoutPositions;
  projectId: string | null;
  refreshWorkspace: (projectId: string, tier: SubscriptionTier) => Promise<void>;
  resetFilters: () => void;
  resetLayout: () => Promise<void>;
  selection: AffinityChartSelection;
  selectEdge: (edgeId: string) => void;
  selectNode: (nodeId: string) => void;
  setNotice: (notice: ProjectAffinityChartNotice | null) => void;
  setPositions: (positions: AffinityChartLayoutPositions) => void;
  snapshot: AffinityChartSnapshot | null;
  status: "idle" | "loading" | "ready" | "error";
  toggleTypeFilter: (type: AffinityChartFilterType) => void;
  updateSavedPositions: (positions: AffinityChartLayoutPositions) => Promise<void>;
}

const defaultDependencies: ProjectAffinityChartDependencies = {
  deleteAffinityChartLayout,
  loadAffinityChartSnapshot,
  saveAffinityChartLayout,
};

function createLockedAccessState(tier: SubscriptionTier): AffinityChartAccessState {
  return {
    recommendedTier: tier === "free" ? "crafter" : "author",
    state: "locked",
    tier,
  };
}

function createAvailableAccessState(tier: SubscriptionTier): AffinityChartAccessState {
  return {
    recommendedTier: null,
    state: "available",
    tier,
  };
}

function buildFiltersForSnapshot(
  snapshot: AffinityChartSnapshot,
  requestedVisibleTypes: AffinityChartFilterType[] | null,
) {
  const availableTypes = new Set(snapshot.filterOptions.map((option) => option.type));
  const requested = requestedVisibleTypes?.filter((type) => availableTypes.has(type)) ?? null;

  if (!requested || (requested.length === 0 && requestedVisibleTypes === null)) {
    return buildDefaultAffinityChartFilters(snapshot.filterOptions);
  }

  return {
    visibleTypes: requested ?? [],
  };
}

export function createProjectAffinityChartStore(
  deps: ProjectAffinityChartDependencies = defaultDependencies,
) {
  const filterMemory = new Map<string, AffinityChartFilterType[]>();

  return createStore<ProjectAffinityChartStoreState>((set, get) => ({
    access: {
      recommendedTier: null,
      state: "idle",
      tier: null,
    },
    errorMessage: null,
    filters: createEmptyAffinityChartFilters(),
    layoutRevision: 0,
    notice: null,
    positions: {},
    projectId: null,
    selection: createEmptyAffinityChartSelection(),
    snapshot: null,
    status: "idle",

    initializeWorkspace: async (projectId, tier) => {
      set({
        access: tier === "free" ? createLockedAccessState(tier) : createAvailableAccessState(tier),
        errorMessage: null,
        notice: null,
        projectId,
        status: tier === "free" ? "ready" : "loading",
      });

      if (tier === "free") {
        set({
          filters: createEmptyAffinityChartFilters(),
          positions: {},
          selection: createEmptyAffinityChartSelection(),
          snapshot: null,
        });
        return;
      }

      try {
        const snapshot = await deps.loadAffinityChartSnapshot(projectId);
        const filters = buildFiltersForSnapshot(snapshot, filterMemory.get(projectId) ?? null);

        set({
          access: createAvailableAccessState(tier),
          errorMessage: null,
          filters,
          positions: snapshot.layoutPositions,
          selection: clampAffinityChartSelection(snapshot, filters, get().selection),
          snapshot,
          status: "ready",
        });
      } catch (error) {
        const tierPrompt = getAffinityChartTierPrompt(error);

        if (tierPrompt) {
          set({
            access: {
              recommendedTier: tierPrompt.recommendedTier,
              state: "locked",
              tier: tierPrompt.tier,
            },
            errorMessage: null,
            filters: createEmptyAffinityChartFilters(),
            positions: {},
            selection: createEmptyAffinityChartSelection(),
            snapshot: null,
            status: "ready",
          });
          return;
        }

        set({
          errorMessage: formatAffinityChartErrorMessage(error),
          snapshot: null,
          status: "error",
        });
      }
    },

    refreshWorkspace: async (projectId, tier) => {
      await get().initializeWorkspace(projectId, tier);
    },

    clearSelection: () => {
      set({
        selection: createEmptyAffinityChartSelection(),
      });
    },

    selectNode: (nodeId) => {
      set({
        selection: {
          kind: "node",
          selectedId: nodeId,
        },
      });
    },

    selectEdge: (edgeId) => {
      set({
        selection: {
          kind: "edge",
          selectedId: edgeId,
        },
      });
    },

    toggleTypeFilter: (type) => {
      const snapshot = get().snapshot;
      const projectId = get().projectId;

      set((state) => {
        const alreadyVisible = state.filters.visibleTypes.includes(type);
        const visibleTypes = alreadyVisible
          ? state.filters.visibleTypes.filter((entry) => entry !== type)
          : [...state.filters.visibleTypes, type];
        const filters = {
          visibleTypes,
        };

        if (snapshot && projectId) {
          filterMemory.set(projectId, visibleTypes);
        }

        return {
          filters,
          selection: snapshot
            ? clampAffinityChartSelection(snapshot, filters, state.selection)
            : state.selection,
        };
      });
    },

    resetFilters: () => {
      const snapshot = get().snapshot;
      const projectId = get().projectId;

      if (!snapshot) {
        set({
          filters: createEmptyAffinityChartFilters(),
        });
        return;
      }

      const filters = buildDefaultAffinityChartFilters(snapshot.filterOptions);
      if (projectId) {
        filterMemory.set(projectId, filters.visibleTypes);
      }

      set((state) => ({
        filters,
        selection: clampAffinityChartSelection(snapshot, filters, state.selection),
      }));
    },

    setPositions: (positions) => {
      set({
        positions,
      });
    },

    updateSavedPositions: async (positions) => {
      const projectId = get().projectId;

      if (!projectId) {
        return;
      }

      set({
        positions,
      });

      try {
        const savedPositions = await deps.saveAffinityChartLayout(projectId, positions);
        set({
          notice: {
            kind: "success",
            message: "affinityChart.messages.layoutSaved",
          },
          positions: savedPositions as AffinityChartLayoutPositions,
        });
      } catch (error) {
        const tierPrompt = getAffinityChartTierPrompt(error);

        if (tierPrompt) {
          set({
            access: {
              recommendedTier: tierPrompt.recommendedTier,
              state: "locked",
              tier: tierPrompt.tier,
            },
            notice: {
              kind: "error",
              message: "affinityChart.messages.tierRequired",
            },
          });
          return;
        }

        set({
          notice: {
            kind: "error",
            message: "affinityChart.messages.saveLayoutError",
          },
        });
      }
    },

    resetLayout: async () => {
      const projectId = get().projectId;

      if (!projectId) {
        return;
      }

      try {
        await deps.deleteAffinityChartLayout(projectId);
        set({
          layoutRevision: get().layoutRevision + 1,
          notice: {
            kind: "success",
            message: "affinityChart.messages.layoutReset",
          },
          positions: {},
        });
      } catch {
        set({
          notice: {
            kind: "error",
            message: "affinityChart.messages.saveLayoutError",
          },
        });
      }
    },

    setNotice: (notice) => {
      set({
        notice,
      });
    },
  }));
}

export const projectAffinityChartStore = createProjectAffinityChartStore();

export function useProjectAffinityChartStore<T>(
  selector: (state: ProjectAffinityChartStoreState) => T,
) {
  return useStore(projectAffinityChartStore, selector);
}
